import {divIcon, LatLngBounds} from "leaflet";
import {useEffect, useMemo, useState, type ReactNode} from "react";
import {CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap} from "react-leaflet";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Checkbox} from "@/components/ui/checkbox";
import {Users} from "lucide-react";
import {cn} from "@/lib/utils";
import {Label} from "@/components/ui/label";
import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";

import {getCoverageShiftTypeCardClass} from "../../lib/coverage-shift-type-styles";
import {ShiftCoverageRow} from "../../types";

const DESK_TOOLBAR_BUTTON_CLASS = "h-7 shrink-0 gap-1 px-2 text-xs";

type DeskLayerToggleProps = {
  id: string;
  active: boolean;
  onToggle: () => void;
  actionButtonClass?: string;
  children: ReactNode;
};

/** Layer chips — same Checkbox + border pattern as coverage page, desk sizing. */
function DeskLayerToggle({id, active, onToggle, actionButtonClass, children}: DeskLayerToggleProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border transition-colors outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        actionButtonClass ?? DESK_TOOLBAR_BUTTON_CLASS,
        active
          ? "border-border bg-muted/60 font-medium text-foreground shadow-xs"
          : "border-border border-dashed bg-background text-muted-foreground opacity-70",
      )}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Checkbox
        id={id}
        checked={active}
        tabIndex={-1}
        className="pointer-events-none size-3.5"
        aria-hidden
      />
      {children}
    </div>
  );
}

function deskToolbarButtonClass(
  baseClassName: string | undefined,
  pressed: boolean,
  shiftType?: ShiftCoverageRow["shift_type"],
) {
  if (pressed && shiftType !== undefined) {
    return cn(baseClassName, getCoverageShiftTypeCardClass(shiftType, true, {accent: false}));
  }

  if (pressed) {
    return cn(
      baseClassName,
      "!border-border !bg-muted font-medium text-foreground shadow-xs",
    );
  }

  if (shiftType !== undefined) {
    return cn(baseClassName, "text-muted-foreground");
  }

  return baseClassName;
}

type CoverageMapProps = {
  rows: ShiftCoverageRow[];
  selectedShiftId: number | null;
  mapHeightClass?: string;
  /** Home preview: map only, no layer toggles or legend chrome. */
  compact?: boolean;
  /** Hide the card title (e.g. when the parent dialog already has a heading). */
  hideTitle?: boolean;
  /** When set, officer chips in the toolbar filter the map. */
  onSelectShift?: (shiftId: number | null) => void;
  /** Outline action sizing — pass dispatch desk header action class in desk dialog. */
  toolbarButtonClassName?: string;
};

const OFFICER_PALETTE = [
  "#2563eb",
  "#0d9488",
  "#7c3aed",
  "#dc2626",
  "#ca8a04",
  "#0891b2",
  "#c026d3",
  "#059669",
];

function getOfficerColor(userId: number) {
  return OFFICER_PALETTE[Math.abs(userId) % OFFICER_PALETTE.length];
}

const OFFICER_LINE_PATTERNS = ["6 8", "2 8", "10 6", "1 7", "12 8 2 8"];

function getOfficerLinePattern(userId: number) {
  return OFFICER_LINE_PATTERNS[Math.abs(userId) % OFFICER_LINE_PATTERNS.length];
}

const ASSIGNED_RING_COLOR = "#1d4ed8";
const ASSIGNED_HALO_RADIUS = 14;
const ASSIGNED_MARKER_RADIUS = 11;
const LAST_KNOWN_FLAG_STROKE = "#ea580c";
const LAST_KNOWN_FLAG_SIZE = {width: 36, height: 42, anchorX: 18, anchorY: 42};
const lastKnownFlagIconCache = new Map<string, ReturnType<typeof divIcon>>();

function getLastKnownFlagIcon(color: string) {
  const cached = lastKnownFlagIconCache.get(color);
  if (cached) return cached;

  const shadowId = `last-known-shadow-${color.replace("#", "")}`;
  const {width, height, anchorX, anchorY} = LAST_KNOWN_FLAG_SIZE;

  const icon = divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));">
      <svg width="${width}" height="${height}" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <filter id="${shadowId}" x="-30%" y="-10%" width="160%" height="130%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="#000" flood-opacity="0.4"/>
          </filter>
        </defs>
        <g filter="url(#${shadowId})">
          <path d="M4 32V5" stroke="#ffffff" stroke-width="5" stroke-linecap="round"/>
          <path d="M4 32V5" stroke="${LAST_KNOWN_FLAG_STROKE}" stroke-width="3" stroke-linecap="round"/>
          <path d="M4 6L23 10.5L4 19Z" fill="#ffffff"/>
          <path d="M4 6L22 10L4 18Z" fill="${color}" stroke="${LAST_KNOWN_FLAG_STROKE}" stroke-width="2.5" stroke-linejoin="round"/>
        </g>
      </svg>
    </div>`,
    iconSize: [width, height],
    iconAnchor: [anchorX, anchorY],
    tooltipAnchor: [0, -anchorY],
  });
  lastKnownFlagIconCache.set(color, icon);
  return icon;
}

function LastKnownLegendFlag() {
  return (
    <span className="flex size-4 items-end justify-center drop-shadow-sm" aria-hidden>
      <svg width="14" height="16" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 32V5" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
        <path d="M4 32V5" stroke={LAST_KNOWN_FLAG_STROKE} strokeWidth="2.5" strokeLinecap="round" />
        <path d="M4 6L22 10L4 18Z" fill="currentColor" className="text-orange-400" stroke={LAST_KNOWN_FLAG_STROKE} strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

type AssignedPropertyMarkerProps = {
  center: [number, number];
  color: string;
  markerKey: string;
  children: ReactNode;
};

function AssignedPropertyMarker({center, color, markerKey, children}: AssignedPropertyMarkerProps) {
  return (
    <>
      <CircleMarker
        key={`${markerKey}-halo`}
        center={center}
        radius={ASSIGNED_HALO_RADIUS}
        pathOptions={{
          color: "#ffffff",
          fillColor: "#ffffff",
          fillOpacity: 1,
          weight: 2,
        }}
        interactive={false}
      />
      <CircleMarker
        key={markerKey}
        center={center}
        radius={ASSIGNED_MARKER_RADIUS}
        pathOptions={{
          color: ASSIGNED_RING_COLOR,
          fillColor: color,
          fillOpacity: 1,
          weight: 3,
        }}
      >
        {children}
      </CircleMarker>
    </>
  );
}

function FitCoverageBounds({points}: {points: [number, number][]}) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    map.fitBounds(new LatLngBounds(points), {padding: [24, 24]});
  }, [map, points]);

  return null;
}

export function CoverageMap({
  rows,
  selectedShiftId,
  mapHeightClass = "h-[34rem]",
  compact = false,
  hideTitle = false,
  onSelectShift,
  toolbarButtonClassName,
}: CoverageMapProps) {
  const isDeskToolbar = Boolean(onSelectShift);
  const actionButtonClass = toolbarButtonClassName ?? DESK_TOOLBAR_BUTTON_CLASS;
  const [showAssigned, setShowAssigned] = useState(true);
  const [showLastKnown, setShowLastKnown] = useState(true);

  const activeRows = useMemo(() => {
    if (selectedShiftId === null) return rows;
    return rows.filter((row) => row.shift_id === selectedShiftId);
  }, [rows, selectedShiftId]);

  const assignedMarkers = useMemo(() => {
    return activeRows.flatMap((row) =>
      [...row.assigned.properties]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((property) => ({
          row,
          property,
          color: getOfficerColor(row.user_id),
        })),
    );
  }, [activeRows]);

  const assignedPositions = assignedMarkers.map(({property}) => [
    property.latitude,
    property.longitude,
  ]) as [number, number][];

  const patrolLines = useMemo(() => {
    return activeRows
      .filter((row) => row.shift_type === "patrol")
      .map((row) => {
        const positions = [...row.assigned.properties]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((property) => [property.latitude, property.longitude] as [number, number]);

        return {
          row,
          positions,
          color: getOfficerColor(row.user_id),
          pattern: getOfficerLinePattern(row.user_id),
        };
      })
      .filter((line) => line.positions.length > 1);
  }, [activeRows]);

  const lastKnownMarkers = useMemo(() => {
    return activeRows
      .filter((row) => row.last_known != null)
      .map((row) => ({
        row,
        position: [row.last_known!.latitude, row.last_known!.longitude] as [number, number],
        color: getOfficerColor(row.user_id),
      }));
  }, [activeRows]);

  const fitPoints = [
    ...(showAssigned ? assignedPositions : []),
    ...(showLastKnown ? lastKnownMarkers.map((marker) => marker.position) : []),
  ];

  const hasSingleSelection = selectedShiftId !== null && activeRows.length === 1;
  const selectedRow = hasSingleSelection ? activeRows[0] : null;
  const uniqueLegendOfficers = useMemo(() => {
    const seen = new Set<number>();
    return activeRows.filter((row) => {
      if (seen.has(row.user_id)) return false;
      seen.add(row.user_id);
      return true;
    });
  }, [activeRows]);

  const mapViewport =
    activeRows.length > 0 ? (
      <div className={cn("relative z-0 overflow-hidden rounded-md border", compact && "h-full min-h-0")}>
        <MapContainer
          center={fitPoints[0] ?? [30.2672, -97.7431]}
          zoom={12}
          scrollWheelZoom={false}
          className={mapHeightClass}
        >
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitCoverageBounds points={fitPoints} />
          {showAssigned
            ? assignedMarkers.map(({row, property, color}) => (
                <AssignedPropertyMarker
                  key={`${row.shift_id}-assigned-${property.property_id}-${property.sort_order}`}
                  markerKey={`${row.shift_id}-assigned-${property.property_id}-${property.sort_order}`}
                  center={[property.latitude, property.longitude]}
                  color={color}
                >
                  <Tooltip>
                    {row.officer_name}: #{property.sort_order} {property.name}
                  </Tooltip>
                </AssignedPropertyMarker>
              ))
            : null}
          {showAssigned
            ? patrolLines.map(({row, positions, pattern}) => (
                <Polyline
                  key={`line-${row.shift_id}`}
                  positions={positions}
                  pathOptions={{color: "#2563eb", dashArray: pattern, weight: 3}}
                />
              ))
            : null}
          {showLastKnown
            ? lastKnownMarkers.map(({row, position, color}) => (
                <Marker
                  key={`last-known-${row.shift_id}`}
                  position={position}
                  icon={getLastKnownFlagIcon(color)}
                  zIndexOffset={1000}
                >
                  <Tooltip>
                    {row.officer_name} last known: {formatDate(row.last_known!.recorded_at)}{" "}
                    {formatTime(row.last_known!.recorded_at)}
                  </Tooltip>
                </Marker>
              ))
            : null}
        </MapContainer>
      </div>
    ) : null;

  if (compact) {
    return mapViewport;
  }

  const layerToggles = isDeskToolbar ? (
    <div className="flex flex-wrap items-center gap-2">
      <DeskLayerToggle
        id="coverage-desk-layer-assigned"
        active={showAssigned}
        actionButtonClass={actionButtonClass}
        onToggle={() => setShowAssigned((current) => !current)}
      >
        <span className="border-background size-3 shrink-0 rounded-full border-2 border-blue-700 bg-blue-600 shadow-sm" />
        Assigned properties
      </DeskLayerToggle>
      <DeskLayerToggle
        id="coverage-desk-layer-last-known"
        active={showLastKnown}
        actionButtonClass={actionButtonClass}
        onToggle={() => setShowLastKnown((current) => !current)}
      >
        <LastKnownLegendFlag />
        Last known
      </DeskLayerToggle>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
        <Checkbox
          id="coverage-layer-assigned"
          checked={showAssigned}
          onCheckedChange={(checked) => setShowAssigned(Boolean(checked))}
        />
        <span className="border-background size-3 shrink-0 rounded-full border-2 border-blue-700 bg-blue-600 shadow-sm" />
        <Label htmlFor="coverage-layer-assigned" className="text-xs">
          Assigned properties
        </Label>
      </div>
      <div className="flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
        <Checkbox
          id="coverage-layer-last-known"
          checked={showLastKnown}
          onCheckedChange={(checked) => setShowLastKnown(Boolean(checked))}
        />
        <LastKnownLegendFlag />
        <Label htmlFor="coverage-layer-last-known" className="text-xs">
          Last known
        </Label>
      </div>
      <span className="text-muted-foreground text-xs">
        {selectedRow ? (
          <>
            Showing markers for <strong>{selectedRow.officer_name}</strong>.
            {!selectedRow.last_known ? <> No recorded position yet.</> : null}
          </>
        ) : (
          <>
            Showing markers for <strong>all filtered officers</strong> ({activeRows.length}).
          </>
        )}
      </span>
    </div>
  );

  const officerFilter = onSelectShift ? (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={deskToolbarButtonClass(actionButtonClass, selectedShiftId === null)}
        aria-pressed={selectedShiftId === null}
        onClick={() => onSelectShift(null)}
      >
        <Users className="size-3.5" aria-hidden />
        All officers
      </Button>
      {rows.map((row) => {
        const isSelected = row.shift_id === selectedShiftId;
        return (
          <Button
            key={row.shift_id}
            type="button"
            variant="outline"
            size="sm"
            className={deskToolbarButtonClass(actionButtonClass, isSelected, row.shift_type)}
            aria-pressed={isSelected}
            onClick={() => onSelectShift(isSelected ? null : row.shift_id)}
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{backgroundColor: getOfficerColor(row.user_id)}}
              aria-hidden
            />
            <span className="max-w-[12rem] truncate">{row.officer_name}</span>
          </Button>
        );
      })}
    </div>
  ) : (
    <div
      className={cn(
        "min-w-0 w-full max-w-full overflow-x-auto",
        activeRows.length === 0 && "hidden",
        selectedRow && "hidden",
      )}
    >
      <div className="flex w-max items-center gap-1.5 pb-1 text-xs">
        {uniqueLegendOfficers.slice(0, 8).map((row) => (
          <span
            key={`legend-user-${row.user_id}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5"
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{backgroundColor: getOfficerColor(row.user_id)}}
            />
            <span className="text-muted-foreground whitespace-nowrap">{row.officer_name}</span>
          </span>
        ))}
        {uniqueLegendOfficers.length > 8 ? (
          <span className="text-muted-foreground shrink-0 whitespace-nowrap">
            +{uniqueLegendOfficers.length - 8} more
          </span>
        ) : null}
      </div>
    </div>
  );

  const toolbar = (
    <div className="flex min-w-0 flex-col gap-3">
      {layerToggles}
      {officerFilter}
    </div>
  );

  const mapBody =
    mapViewport ?? (
      <div className="text-muted-foreground rounded-md border border-dashed p-6 text-sm">
        No active shift coverage found for the selected filters.
      </div>
    );

  if (hideTitle) {
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {toolbar}
        {mapBody}
      </div>
    );
  }

  return (
    <Card className="min-w-0 overflow-hidden py-4">
      <CardHeader className="min-w-0 gap-3">
        <CardTitle className="text-base">Coverage Map</CardTitle>
        {toolbar}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">{mapBody}</CardContent>
    </Card>
  );
}
